/* eslint-disable max-lines */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  Chip,
  Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InsightsIcon from '@mui/icons-material/Insights';
import CreateIcon from '@mui/icons-material/Create';
import MicIcon from '@mui/icons-material/Mic';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PsychologyIcon from '@mui/icons-material/Psychology';
import StorageIcon from '@mui/icons-material/Storage';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GroupIcon from '@mui/icons-material/Group';
import SecurityIcon from '@mui/icons-material/Security';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import SchoolIcon from '@mui/icons-material/School';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';

// eslint-disable-next-line max-lines-per-function
export const Help = () => {
  const [expanded, setExpanded] = useState('getting-started');

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <HelpIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">Help & Support</Typography>
          <Typography variant="body2" color="text.secondary">
            Your guide to the Faana Growth OS platform
          </Typography>
        </Box>
      </Box>
      {/* Quick Access Alert */}
      <Alert severity="info" icon={<TipsAndUpdatesIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2" component="div">
          <strong>Tip:</strong> Press{' '}
          <Chip size="small" label="Cmd+K" sx={{ mx: 0.5 }} /> (Mac) or{' '}
          <Chip size="small" label="Ctrl+K" sx={{ mx: 0.5 }} /> (Windows) to open the command palette for quick navigation
        </Typography>
      </Alert>
      {/* Section 1: Getting Started */}
      <Accordion
        expanded={expanded === 'getting-started'}
        onChange={handleAccordionChange('getting-started')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <RocketLaunchIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6">Getting Started</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Welcome to Faana Growth OS
          </Typography>
          <Typography variant="body2" paragraph>
            Faana Growth OS is an enterprise platform for organizational growth and leadership development.
            Our platform uses neuroscience-based methodologies to transform leaders and organizations through
            data-driven insights and comprehensive entity relationship tracking.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            First Steps
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="1. Select Your Entity"
                secondary="Use the entity selector in the top-right corner to choose which organization, person, program, or community you're working with. This selection determines the context for all your activities."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="2. Explore Insights"
                secondary="Navigate to the Insights dashboard to view analytics, metrics, and growth indicators for your selected entity. This is your command center for understanding organizational performance."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="3. Capture Information"
                secondary="Use our capture tools to record audio conversations, upload documents, or write notes. All captured information is automatically processed and linked to your selected entity."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="4. Engage with AI Chat"
                secondary="Ask questions and get AI-powered insights about your entity data. The chat assistant can help analyze information, generate reports, and provide recommendations."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Navigation Basics
          </Typography>
          <Typography variant="body2" paragraph>
            The platform uses a dual navigation system:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Bottom Navigation (Mobile) / Side Navigation (Desktop)"
                secondary="Access the four primary features: Insights, Capture, Chat, and Entities"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Hamburger Menu"
                secondary="Access secondary features: Faana Agents, Data Sources (admin only), and Help"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>
      {/* Section 2: Core Features */}
      <Accordion
        expanded={expanded === 'core-features'}
        onChange={handleAccordionChange('core-features')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <InsightsIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6">Core Features</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Insights */}
            <Grid size={12}>
              <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InsightsIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Insights Dashboard</Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  The Insights dashboard provides comprehensive analytics and visualizations for your selected entity:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Entity Metrics Bar - Key performance indicators and growth metrics" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Relationship Visualizations - Interactive chord diagrams and network graphs showing entity connections" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Growth Trend Analysis - Time-series charts tracking progress over time" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Investment Impact Analysis - Scatter plots showing correlation between investment and outcomes" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Hierarchical Data Views - Sunburst charts for understanding organizational structure" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Capture */}
            <Grid size={12}>
              <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CreateIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Capture Tools</Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Three powerful ways to capture and process information:
                </Typography>

                <Box sx={{ ml: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <MicIcon sx={{ mr: 1, color: 'action.active', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">Audio Recording</Typography>
                      <Typography variant="body2">
                        Record conversations, meetings, and coaching sessions. Audio is automatically transcribed
                        and processed to extract entities, insights, and action items. Supports real-time recording
                        with visual feedback.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <UploadFileIcon sx={{ mr: 1, color: 'action.active', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">File Upload</Typography>
                      <Typography variant="body2">
                        Upload documents, spreadsheets, PDFs, and other files for processing. Supported formats
                        include .txt, .md, .html, .json, .pdf, .docx, and .xlsx. Files are automatically analyzed
                        for entity relationships and sensitive data is tokenized for privacy.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <MenuBookIcon sx={{ mr: 1, color: 'action.active', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">Notebook</Typography>
                      <Typography variant="body2">
                        Create rich text notes with formatting, lists, and embedded content. Perfect for
                        documenting observations, meeting notes, and insights. Notes are automatically linked
                        to your selected entity and can include file attachments.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Chat */}
            <Grid size={12}>
              <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SmartToyIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">AI Chat Assistant</Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Your intelligent partner for insights and actions:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Ask questions about entity data, relationships, and performance" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Generate reports and documents using AI-powered templates" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Perform actions through Model Context Protocol (MCP) tools" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Access historical context from previous conversations" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Get recommendations based on neuroscience-backed methodologies" />
                  </ListItem>
                </List>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>MCP Tools:</strong> The chat assistant can perform actions like creating documents,
                    managing templates, and updating entity notes. Destructive actions require confirmation.
                  </Typography>
                </Alert>
              </Paper>
            </Grid>

            {/* Entities */}
            <Grid size={12}>
              <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountTreeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Entity Management</Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Manage and understand your organizational ecosystem:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Entity Types"
                      secondary="Organizations, People, Programs, and Communities"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Entity Creation"
                      secondary="Multi-step form with automatic resource provisioning (BigQuery datasets, Cloud Storage buckets)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Entity Discovery"
                      secondary="AI-powered discovery of entities and relationships from uploaded documents"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Permission Management"
                      secondary="Role-based access control with admin, reports, data, and viewer roles"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Relationship Mapping"
                      secondary="Track and visualize connections between entities"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* Section 3: Advanced Features */}
      <Accordion
        expanded={expanded === 'advanced-features'}
        onChange={handleAccordionChange('advanced-features')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <PsychologyIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6">Advanced Features</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Faana Agents */}
            <Grid size={12}>
              <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PsychologyIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Faana Agents</Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  AI-powered agents that automate complex workflows:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Token Examiner"
                      secondary="Discovers entities and relationships in documents using embeddings and AI analysis"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Document Generator"
                      secondary="Creates professional reports and documents using AI templates and entity data"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Privacy Tokenization"
                      secondary="Automatically identifies and tokenizes sensitive information for compliance"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Data Ingestion"
                      secondary="Automated syncing from Google Drive and other data sources"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Data Sources (Admin Only) */}
            <Grid size={12}>
              <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    Data Sources
                    <Chip label="Admin Only" size="small" color="error" sx={{ ml: 1 }} />
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Centralized data management for administrators:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="BigQuery Datasets"
                      secondary="View and manage entity-specific BigQuery datasets for analytics"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Cloud Storage Buckets"
                      secondary="Browse and manage files in Google Cloud Storage"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Firestore Collections"
                      secondary="View document collections and real-time data"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Ingestion Points"
                      secondary="Configure automated data sync from external sources"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* Section 4: Faana Methodology */}
      <Accordion
        expanded={expanded === 'methodology'}
        onChange={handleAccordionChange('methodology')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SchoolIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6">The Faana Methodology</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Neuroscience-Based Leadership Development
          </Typography>
          <Typography variant="body2" paragraph>
            Faana's approach to organizational transformation is grounded in neuroscience and behavioral psychology.
            Our platform supports this methodology through data-driven insights and structured programs.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Core Principles
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Prefrontal Cortex Activation"
                secondary="Techniques to enhance executive function, decision-making, and emotional regulation through targeted exercises and practices"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Growth Operating System (GOS)"
                secondary="A comprehensive framework for sustainable organizational growth combining leadership development, process optimization, and cultural transformation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Holistic Growth Framework"
                secondary="Integration of personal, professional, and organizational development for comprehensive transformation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Community-Based Programming"
                secondary="Leveraging peer support and collective learning for accelerated growth and accountability"
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Key Programs
          </Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  90-Day Programs
                </Typography>
                <Typography variant="body2">
                  Intensive transformation experiences with structured milestones, weekly coaching,
                  and measurable outcomes for leaders and teams.
                </Typography>
              </Paper>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Growth Workshops
                </Typography>
                <Typography variant="body2">
                  Interactive sessions for CEOs, founders, and team leads focusing on practical
                  application of growth principles.
                </Typography>
              </Paper>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Rising Leader Programs
                </Typography>
                <Typography variant="body2">
                  Development pathways for emerging leaders with mentorship, skill-building,
                  and real-world application.
                </Typography>
              </Paper>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Accelerator Programs
                </Typography>
                <Typography variant="body2">
                  Fast-track development for high-potential individuals and teams with intensive
                  coaching and support.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Assessment Tools
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="FIT Assessment"
                secondary="Comprehensive evaluation of individual and organizational readiness for growth"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Leadership Risk & Growth Index"
                secondary="Diagnostic tool for identifying leadership gaps and growth opportunities"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Leadership Dynamics Assessment"
                secondary="Analysis of team dynamics and leadership effectiveness"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>
      {/* Section 5: User Roles & Permissions */}
      <Accordion
        expanded={expanded === 'roles'}
        onChange={handleAccordionChange('roles')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <GroupIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6">User Roles & Permissions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Role-Based Access Control
          </Typography>
          <Typography variant="body2" paragraph>
            The platform uses a sophisticated permission system to ensure data security and appropriate access levels.
          </Typography>

          <Grid container spacing={2}>
            {/* Platform Admin */}
            <Grid size={12}>
              <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'error.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AdminPanelSettingsIcon sx={{ mr: 1, color: 'error.main' }} />
                  <Typography variant="h6">Platform Administrator</Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Full system access with platform-wide administrative capabilities:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Manage all entities and their permissions" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Access Data Sources management interface" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Configure system-wide settings and integrations" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="View comprehensive audit logs" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Manage user roles and platform access" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Access all tokenized data including sensitive information" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Entity-Specific Roles */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'warning.main' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Entity Admin
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Full access to entity data and settings" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Manage entity permissions for other users" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Access all entity-specific features" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="View sensitive tokenized data" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'success.main' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Reports Role
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Generate and view reports" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Access analytics and insights" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="View organizational data" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Limited access to sensitive data" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'info.main' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Data Role
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Upload and manage documents" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Access data management tools" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="View general tokenized data" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Create and edit notes" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper sx={{ p: 2, borderLeft: 4, borderColor: 'grey.500' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Viewer Role
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Read-only access to general data" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="View public insights and reports" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Access chat for queries" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="No data modification capabilities" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Only users with @faanaworks.com email addresses can authenticate to the platform.
              Contact your platform administrator for access requests.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>
      {/* Section 6: Troubleshooting */}
      <Accordion
        expanded={expanded === 'troubleshooting'}
        onChange={handleAccordionChange('troubleshooting')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <BugReportIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6">Troubleshooting</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Common Issues and Solutions
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Authentication Issues"
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <>
                    <Typography variant="body2" paragraph component="div">
                      <strong>Problem:</strong> "User not authenticated" error
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>Solution:</strong> Your session may have expired. Sign out and sign back in.
                      If the issue persists, check that you're using a @faanaworks.com email address.
                    </Typography>
                  </>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <AccountTreeIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="Entity Not Selected"
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <>
                    <Typography variant="body2" paragraph component="div">
                      <strong>Problem:</strong> Features not working or showing "Please select an entity"
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>Solution:</strong> Use the entity selector in the top-right corner to choose an entity.
                      Most features require an entity context to function properly.
                    </Typography>
                  </>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <SecurityIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Access Denied"
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <>
                    <Typography variant="body2" paragraph component="div">
                      <strong>Problem:</strong> "Access denied" or "Insufficient permissions" error
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>Solution:</strong> You may not have the required role for this action.
                      Contact your entity administrator or platform administrator to request appropriate permissions.
                    </Typography>
                  </>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <UploadFileIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="File Upload Issues"
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <>
                    <Typography variant="body2" paragraph component="div">
                      <strong>Problem:</strong> File upload fails or times out
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>Solution:</strong> Check file size (max 10MB for most files, 50MB for PDFs).
                      Ensure the file format is supported (.txt, .md, .html, .json, .pdf, .docx, .xlsx).
                      Large files may take time to process.
                    </Typography>
                  </>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <SmartToyIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Chat Not Responding"
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <>
                    <Typography variant="body2" paragraph component="div">
                      <strong>Problem:</strong> AI Chat assistant not responding or showing errors
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>Solution:</strong> Refresh the page to reset the chat session.
                      If the issue persists, check your internet connection.
                      Complex queries may take longer to process.
                    </Typography>
                  </>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <StorageIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="Data Not Showing"
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <>
                    <Typography variant="body2" paragraph component="div">
                      <strong>Problem:</strong> Dashboard or data views showing no data
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>Solution:</strong> Verify you have selected an entity.
                      Data may still be processing if recently uploaded.
                      Check your role permissions for the selected entity.
                    </Typography>
                  </>
                }
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Performance Tips
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Use Chrome or Firefox"
                secondary="For best performance and compatibility, use the latest version of Chrome or Firefox"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Clear Browser Cache"
                secondary="If experiencing unexpected behavior, clear your browser cache and cookies for this site"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Check Network Connection"
                secondary="A stable internet connection is required for real-time features and file uploads"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Disable Browser Extensions"
                secondary="Some ad blockers or privacy extensions may interfere with platform functionality"
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Still need help?</strong> Contact your platform administrator or email support@faanaworks.com
              with details about your issue, including any error messages and steps to reproduce the problem.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>
      {/* Section 7: Keyboard Shortcuts */}
      <Accordion
        expanded={expanded === 'shortcuts'}
        onChange={handleAccordionChange('shortcuts')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <InfoIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6">Keyboard Shortcuts & Tips</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Productivity Shortcuts
          </Typography>

          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Navigation
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    primary={<Chip size="small" label="Cmd/Ctrl + K" />}
                    secondary="Open command palette"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    primary={<Chip size="small" label="Cmd/Ctrl + /" />}
                    secondary="Toggle chat window"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    primary={<Chip size="small" label="Esc" />}
                    secondary="Close dialogs and overlays"
                  />
                </ListItem>
              </List>
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Actions
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    primary={<Chip size="small" label="Cmd/Ctrl + Enter" />}
                    secondary="Submit forms and send chat messages"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    primary={<Chip size="small" label="Cmd/Ctrl + S" />}
                    secondary="Save current work (in editors)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    primary={<Chip size="small" label="Tab" />}
                    secondary="Navigate between form fields"
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Pro Tips
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <TipsAndUpdatesIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Use the command palette"
                secondary="Quickly navigate to any feature by typing its name in the command palette (Cmd/Ctrl + K)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <TipsAndUpdatesIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Bookmark frequently used entities"
                secondary="Star your most-used entities for quick access in the entity selector"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <TipsAndUpdatesIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Leverage chat for quick actions"
                secondary="Ask the AI chat to perform actions like 'Generate a 30-day report' or 'Show me recent uploads'"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <TipsAndUpdatesIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Use filters in data views"
                secondary="All data tables support filtering and sorting - click column headers to sort"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>
      {/* Contact Support */}
      <Paper sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
        <Typography variant="h6" gutterBottom>
          Need Additional Support?
        </Typography>
        <Typography variant="body1" paragraph>
          Our support team is here to help you succeed with the Faana Growth OS platform.
        </Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Technical Support
            </Typography>
            <Typography variant="body2">
              Email: support@faanaworks.com
            </Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Platform Administrator
            </Typography>
            <Typography variant="body2">
              Email: fgos@faanaworks.com
            </Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Training & Onboarding
            </Typography>
            <Typography variant="body2">
              Email: training@faanaworks.com
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

